import { InfoCircleOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StockBalancesAPI } from '@/api';
import { store, useAppDispatch, useAppState } from '@/store';
import { Tooltip } from '@/ui';
import { toNumber } from '@/utils';
import { StockBalancesList, StockBalancesSummary } from '../../components';
import { GetStockBalancesListDTO } from '../../dtos/GetStockBalancesListDTO';
import { changeQuery, getStockBalances, resetState } from '../../feature';
import { selectQuery, stockBalancesSelector } from '../../feature/selectors';
import { StockBalancesTab, TGetStockBalancesQuery } from '../../helpers/types';
import {
  StyledContentBlock,
  StyledMainTitleWrapper,
  StyledMenu,
  StyledPageTitle,
  StyledPageWrapper,
  StyledTabLabel,
} from './StockBalancesMainContainerStyles';

const StockBalancesMainContainer = () => {
  const { t } = useTranslation(['stockBalances', 'common']);
  const dispatch = useAppDispatch();
  const { list, pagination, isLoading } = useAppState(stockBalancesSelector);
  const [regionOptions, setRegionOptions] = useState<{ label: string; value: string }[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const [filterRevision, setFilterRevision] = useState(0);

  const tabItems = useMemo(
    () => [
      { key: StockBalancesTab.PICKUP_POINTS, label: t('tab_pickup_points') },
      {
        key: StockBalancesTab.SUPPLIER_WAREHOUSES,
        disabled: true,
        label: (
          <StyledTabLabel>
            {t('tab_supplier_warehouses')}
            <Tooltip title={t('tab_supplier_warehouses_tooltip')}>
              <InfoCircleOutlined />
            </Tooltip>
          </StyledTabLabel>
        ),
      },
    ],
    [t],
  );

  const bumpFilterRevision = () => {
    setFilterRevision((revision) => revision + 1);
  };

  const fetchList = useCallback(
    (params: Partial<TGetStockBalancesQuery>) => {
      const currentQuery = selectQuery(store.getState()) ?? {};
      const mergedQuery = { ...currentQuery, ...params };
      const dto = new GetStockBalancesListDTO(mergedQuery);
      dispatch(changeQuery(mergedQuery));
      dispatch(getStockBalances(dto));
    },
    [dispatch],
  );

  useEffect(() => {
    StockBalancesAPI.getStockRegions().then((response) => {
      setRegionOptions((response.data.data ?? []).map((region) => ({ label: region.name ?? '', value: region.id })));
    });
    StockBalancesAPI.getStockCategories().then((response) => {
      setCategoryOptions(
        (response.data.data ?? []).map((category) => ({ label: category.name ?? '', value: category.id })),
      );
    });
    fetchList({ page: 1 });

    return () => {
      dispatch(resetState());
    };
  }, [dispatch, fetchList]);

  const handlePageChange = (page: number) => {
    bumpFilterRevision();
    fetchList({ page });
  };

  return (
    <StyledPageWrapper>
      <StyledMainTitleWrapper>
        <StyledPageTitle>{t('page_title')}</StyledPageTitle>
        <StyledMenu mode="horizontal" items={tabItems} selectedKeys={[StockBalancesTab.PICKUP_POINTS]} />
      </StyledMainTitleWrapper>
      <StyledContentBlock>
        <StockBalancesSummary totalSum={toNumber(pagination?.stock_value_total)} isLoaded={pagination != null} />
        <StockBalancesList
          list={list ?? []}
          isLoading={isLoading}
          currentPage={pagination?.current_page ?? 1}
          total={pagination?.total ?? 0}
          pageSize={pagination?.per_page ?? 25}
          onPageChange={handlePageChange}
          regionOptions={regionOptions}
          categoryOptions={categoryOptions}
          filterRevision={filterRevision}
        />
      </StyledContentBlock>
    </StyledPageWrapper>
  );
};

export default StockBalancesMainContainer;
